import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Spinner, Text, VStack } from "@chakra-ui/react";
import { useAppContext } from "../context/AppContext";
import { fetchToken } from "../apis/token-api";

const TRUSTED_PORTAL_ORIGINS = new Set([
  "http://localhost:3000",
  "https://cenze-links.vercel.app",
  "https://portalcenze.vercel.app",
]);

const PortalSsoPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setGlobalToken, setUsername, setPassword } = useAppContext();

  useEffect(() => {
    let isActive = true;
    const persistSession = (portalUser, portalPassword, tokenPayload) => {
      if (!isActive) return;
      setUsername(portalUser);
      setPassword(portalPassword);
      localStorage.setItem("portal_username", portalUser);
      localStorage.setItem("portal_password", portalPassword);
      if (tokenPayload.refresh_token) {
        localStorage.setItem("portal_refresh_token", tokenPayload.refresh_token);
      }
      localStorage.setItem("portal_token_payload", JSON.stringify(tokenPayload));
      setGlobalToken(tokenPayload);
      navigate("/home", { replace: true });
    };

    const handleCredentialMessage = async (event) => {
      if (!TRUSTED_PORTAL_ORIGINS.has(event.origin)) {
        return;
      }
      const data = event.data || {};
      if (data.type === "PORTAL_SSO_SESSION") {
        if (!data.token) {
          return;
        }
        const tokenPayload = {
          access_token: data.token,
          refresh_token: data.refreshToken || "",
          token_type: data.tokenType || "Bearer",
          scope: data.scope || "default",
          expires_in: Number(data.expiresIn || 3600)
        };
        persistSession(data.username || "PORTAL", "__portal_sso__", tokenPayload);
        return;
      }
      if (data.type !== "PORTAL_SSO_CREDENTIALS") {
        return;
      }
      if (!data.username || !data.password) {
        return;
      }
      try {
        const tokenData = await fetchToken(data.username, data.password);
        const tokenPayload = {
          access_token: tokenData.access_token,
          refresh_token: tokenData.refresh_token || "",
          token_type: tokenData.token_type || "Bearer",
          scope: tokenData.scope || "default",
          expires_in: Number(tokenData.expires_in || 3600)
        };
        persistSession(data.username, data.password, tokenPayload);
      } catch (error) {
        console.error("Falha ao autenticar via credenciais do portal.", error);
      }
    };

    window.addEventListener("message", handleCredentialMessage);

    const portalToken = searchParams.get("portalToken");
    const portalRefreshToken = searchParams.get("portalRefreshToken");
    const portalExpiresIn = Number(searchParams.get("portalExpiresIn") || "3600");
    const portalTokenType = searchParams.get("portalTokenType") || "Bearer";
    const portalScope = searchParams.get("portalScope") || "default";
    const portalUser = searchParams.get("portalUser") || "PORTAL";

    if (portalToken && !portalToken.startsWith("eyJtb2Nr")) {
      const tokenPayload = {
        access_token: portalToken,
        refresh_token: portalRefreshToken || "",
        token_type: portalTokenType,
        scope: portalScope,
        expires_in: Number.isFinite(portalExpiresIn) ? portalExpiresIn : 3600
      };
      persistSession(portalUser, "__portal_sso__", tokenPayload);
    }

    return () => {
      isActive = false;
      window.removeEventListener("message", handleCredentialMessage);
    };
  }, [navigate, searchParams, setGlobalToken, setPassword, setUsername]);

  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
      <VStack spacing={4}>
        <Spinner size="xl" />
        <Text>Conectando com o portal...</Text>
      </VStack>
    </Box>
  );
};

export default PortalSsoPage;
